import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { SettingsCustomFormsRequests } from './custom-forms.requests';

import * as fromStore from './custom-forms.state';
import * as fromActions from './custom-forms.actions';
import * as Objects from '@core/objects/form';

import { FieldDataInterface } from '@core/custom-forms/models/settings/settings-custom-form-field-data.model';
import { SettingsCustomFormFieldData } from "@core/custom-forms/models/settings/settings-custom-form-field-data.class";
import { FieldFactoryLocator } from '@core/custom-forms/factories/field-factory-locator';

@Injectable()
export class SettingsCustomFormsEffects {

    constructor(private actions$: Actions, private store$: Store<fromStore.SettingsCustomForms>, 
        private requests: SettingsCustomFormsRequests,
        private fieldFactoryLocator: FieldFactoryLocator
    ) {}

    /**
     * Persisted forms effects
     */

    public loadForms$ : Observable<fromActions.SettingsFormsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadForms>(fromActions.SettingsFormsActionsType.LoadForms),
        mergeMap((action) => {
            return this.requests.loadForms$().pipe(
                map((forms: Objects.Form[]) => {
                    return new fromActions.LoadFormsSuccess(forms);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadFormsError(err));
                })
            );
        })
    ));

    // select the form from the list. This not be called when initialized is false
    public loadForm$ : Observable<fromActions.SettingsFormsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadForm>(fromActions.SettingsFormsActionsType.LoadForm),
        withLatestFrom(this.store$.select(fromStore.settingsFormsFormsSelector )),
        mergeMap(([action, forms]) => {
            const form = forms.find((cf: Objects.Form) => cf.id === action.formId);
            if (form)
                return [new fromActions.LoadFormSuccess(form)];
            else 
                return [new fromActions.LoadFormError()];
        })
    ));


    // saves form
    public saveForm$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SaveForm>(fromActions.SettingsFormsActionsType.SaveForm),
        withLatestFrom(this.store$.select(fromStore.settingsFormsEditingSelector )),
        mergeMap(([action, editing]) => {
            // check for new form to make POST
            if(editing.form.id != null){
                // MAKE PUT only when has changes
                return of( editing.form?.changed ? new fromActions.UpdateForm(action.form) : new fromActions.SaveFormSuccess(action.form))
            } else {
                return of(new fromActions.CreateForm(action.form))
            }
          
        })
    ));

    public createForm$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.CreateForm>(fromActions.SettingsFormsActionsType.CreateForm),
        mergeMap((action) => {
            return this.requests.createForm$(action.form).pipe(
                map((formCreated: Objects.Form) => {
                    return new fromActions.SaveFormSuccess(formCreated, action.form.id === null);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SaveFormError(err));
                })
            );
        })
    ));

    public updateForm$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateForm>(fromActions.SettingsFormsActionsType.UpdateForm),
        mergeMap((action) => {
            return this.requests.updateForm$(action.form).pipe(
                map((formUpdated: Objects.Form) => {
                    return new fromActions.SaveFormSuccess(formUpdated);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SaveFormError(err));
                })
            );
        })
    ));

    public saveFormSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SaveFormSuccess>(fromActions.SettingsFormsActionsType.SaveFormSuccess),
        switchMap((action) => {
            return  [new fromActions.SaveFields(action.newForm)];
            })
    ));

    /**
     * Persisted fields effects
     */

    public loadFields$ : Observable<fromActions.SettingsFormsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadFields>(fromActions.SettingsFormsActionsType.LoadFields),
        mergeMap((action) => {
            return this.requests.loadFields$(action.formId).pipe(
                map((fields: FieldDataInterface[]) => {
                    const loadedFieldsInstances = fields.map(f => this.fieldFactoryLocator.get(f.type).deserializeSettingsFormField(f));
                    return new fromActions.LoadFieldsSuccess(loadedFieldsInstances);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadFieldsError(err));
                })
            );
        })
    ));
    // saves fields 
    public saveFields$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SaveFields>(fromActions.SettingsFormsActionsType.SaveFields),
        withLatestFrom(this.store$.select(fromStore.settingsFormsEditingSelector )),
        mergeMap(([action, editing]) => {
            // check for POST fist time when is a new form.
            if(action.newForm)
                return of(new fromActions.CreateFields(editing.form.id, editing.fields));
            else { // in case updateing we use PUT to update ONLY modified 
                const fieldsToSave: SettingsCustomFormFieldData[] = editing.fields.filter((ef: SettingsCustomFormFieldData) => ef.changed);
                // MAKE PUT only when some fields had changed
                if (fieldsToSave.length > 0){
                    return of(new fromActions.UpdateFields(editing.form.id, fieldsToSave));
                } else {
                    // prevent post or put when no changed fields
                    return of(new fromActions.SaveFieldsSuccess([]));
                }
            }
        })
    ));

    public createFields$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.CreateFields>(fromActions.SettingsFormsActionsType.CreateFields),
        mergeMap((action) => {
            const serializedFieldsToCreate: Array<FieldDataInterface> = action.fields.map(f => f.serialize());
            return this.requests.createFields$(action.formId, serializedFieldsToCreate).pipe(
                map((created: FieldDataInterface[]) => {
                    const deserializedCreatedFields = created.map(f => this.fieldFactoryLocator.get(f.type).deserializeSettingsFormField(f));
                    //     const updatedField = action.fields.find((f) => f.id === field.id);
                    //     return updatedField ?? field;
                    // }),
                    return new fromActions.SaveFieldsSuccess(deserializedCreatedFields);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SaveFieldsError(err));
                })
            );
        })
    ));
    public updateFields$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateFields>(fromActions.SettingsFormsActionsType.UpdateFields),
        mergeMap((action) => {
            const serializedFieldsToUpdate: Array<FieldDataInterface> = action.fields.map(f => f.serialize());
            return this.requests.updateFields$(action.formId, serializedFieldsToUpdate).pipe(
                map((updatedFields: FieldDataInterface[]) => {
                    const deserializedUpdatedFields = updatedFields.map(f => this.fieldFactoryLocator.get(f.type).deserializeSettingsFormField(f));
                    return new fromActions.SaveFieldsSuccess(deserializedUpdatedFields);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SaveFieldsError(err));
                })
            );
        })
    ));

    public setActiveForm$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetActiveForm>(fromActions.SettingsFormsActionsType.SetActiveForm),
        mergeMap((action) => {
         
            return this.requests.setActiveForm$(action.formId, action.active).pipe(
                map((form: Objects.Form) => {
                    return new fromActions.SetActiveFormSuccess(form);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetActiveFormError(err));
                })
            );
            
        })
    ));

    public setActiveFormError$ : Observable<fromActions.SettingsFormsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetActiveFormError>(fromActions.SettingsFormsActionsType.SetActiveFormError),
        mergeMap((action) => {
            return [new fromActions.LoadForms()];
        })
    ));

}